/**
 * outbreak-cache — IndexedDB persistence for the last successful
 * /api/outbreak payload.
 *
 * Written by `apps/web/src/services/outbreak.ts` on every successful
 * network fetch (fire-and-forget, never blocks the UI promise).
 *
 * Read by `useOfflineCachedOutbreak()` when the device is offline or
 * the SSE stream has been silent for >15 s.
 *
 * Database : hantavirus-outbreak-cache
 * Store    : payloads
 * Key      : 'latest'  (single slot — always overwrites)
 */

import { openDB, type IDBPDatabase } from "idb";
import type { OutbreakResponse } from "#/services/outbreak.js";

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

const DB_NAME = "hantavirus-outbreak-cache";
const DB_VERSION = 1;
const STORE_NAME = "payloads";
const KEY = "latest";

export interface CachedOutbreakEntry {
  payload: OutbreakResponse;
  fetchedAt: number; // Unix ms timestamp
}

// ---------------------------------------------------------------------------
// DB singleton (lazy open, never re-opened on the server)
// ---------------------------------------------------------------------------

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDb(): Promise<IDBPDatabase> {
  if (typeof window === "undefined") {
    // SSR guard — IndexedDB is not available in Node.
    return Promise.reject(new Error("IndexedDB unavailable outside browser"));
  }
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      },
    });
  }
  return dbPromise;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Read the cached outbreak payload. Returns `null` if the cache is empty
 * (first visit, or cache cleared). Never throws — callers treat null as
 * "no cache available".
 */
export async function getCachedOutbreak(): Promise<CachedOutbreakEntry | null> {
  try {
    const db = await getDb();
    const entry = await db.get(STORE_NAME, KEY);
    return (entry as CachedOutbreakEntry) ?? null;
  } catch {
    return null;
  }
}

/**
 * Write a new cached outbreak payload. Overwrites the previous entry.
 * Callers should fire-and-forget this (`.catch(...)`) so it never blocks
 * the user-visible resolved promise.
 */
export async function setCachedOutbreak(
  payload: OutbreakResponse,
  fetchedAt: number,
): Promise<void> {
  const db = await getDb();
  const entry: CachedOutbreakEntry = { payload, fetchedAt };
  await db.put(STORE_NAME, entry, KEY);
}

/**
 * Clear the cache. Used in tests and developer tooling. Not called in
 * production flows.
 */
export async function clearOutbreakCache(): Promise<void> {
  try {
    const db = await getDb();
    await db.delete(STORE_NAME, KEY);
  } catch {
    // Ignore — clearing a missing cache is a no-op.
  }
}
